import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Gift, Heart, CreditCard, Loader2, CheckCircle2, Sparkles } from 'lucide-react';
import axios from 'axios';
import SEOHead, { pageSEO, generateBreadcrumbSchema } from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + '/api';

function VoucherTypeSelector({ voucherType, setVoucherType, setSelectedSession, setSelectedAmount, setCustomAmount }) {
    const handleMonetaryClick = () => {
        setVoucherType('monetary');
        setSelectedSession(null);
    };
    
    const handleSessionClick = () => {
        setVoucherType('session');
        setSelectedAmount(null);
        setCustomAmount('');
    };

    return (
        <div className="grid grid-cols-2 gap-4">
            <button
                type="button"
                onClick={handleMonetaryClick}
                className={'p-4 rounded-xl border-2 transition-all ' + (voucherType === 'monetary' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50')}
                data-testid="voucher-type-monetary"
            >
                <CreditCard className={'w-8 h-8 mx-auto mb-2 ' + (voucherType === 'monetary' ? 'text-primary' : 'text-muted-foreground')} />
                <p className="font-semibold">Monetary</p>
                <p className="text-sm text-muted-foreground">Choose any amount</p>
            </button>
            <button
                type="button"
                onClick={handleSessionClick}
                className={'p-4 rounded-xl border-2 transition-all ' + (voucherType === 'session' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50')}
                data-testid="voucher-type-session"
            >
                <Gift className={'w-8 h-8 mx-auto mb-2 ' + (voucherType === 'session' ? 'text-primary' : 'text-muted-foreground')} />
                <p className="font-semibold">Session</p>
                <p className="text-sm text-muted-foreground">Gift a specific session</p>
            </button>
        </div>
    );
}

function MonetaryAmountSelector({ options, selectedAmount, setSelectedAmount, customAmount, setCustomAmount }) {
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-4 gap-2">
                {options.map(function(amount) {
                    const isSelected = selectedAmount === amount;
                    return (
                        <button
                            key={amount}
                            type="button"
                            onClick={function() { setSelectedAmount(amount); setCustomAmount(''); }}
                            className={'py-3 px-4 rounded-lg border-2 font-semibold transition-all ' + (isSelected ? 'border-primary bg-primary text-white' : 'border-border hover:border-primary/50')}
                            data-testid={'amount-' + amount}
                        >
                            £{amount}
                        </button>
                    );
                })}
            </div>
            <div className="flex items-center gap-2">
                <span className="text-muted-foreground">or</span>
                <div className="flex-1 flex items-center gap-2">
                    <span className="text-lg font-semibold">£</span>
                    <Input
                        type="number"
                        min="10"
                        placeholder="Custom amount"
                        value={customAmount}
                        onChange={function(e) { setCustomAmount(e.target.value); setSelectedAmount('custom'); }}
                        className="flex-1"
                        data-testid="custom-amount-input"
                    />
                </div>
            </div>
        </div>
    );
}

function SessionSelector({ sessions, selectedSession, setSelectedSession }) {
    return (
        <div className="space-y-3">
            {sessions.map(function(session) {
                const isSelected = selectedSession && selectedSession.id === session.id;
                return (
                    <button
                        key={session.id}
                        type="button"
                        onClick={function() { setSelectedSession(session); }}
                        className={'w-full p-4 rounded-lg border-2 text-left transition-all ' + (isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50')}
                        data-testid={'session-' + session.id}
                    >
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="font-semibold">{session.name}</p>
                            </div>
                            <Badge variant="secondary" className="text-lg">
                                £{session.price}
                            </Badge>
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

export default function GiftVouchers() {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [options, setOptions] = useState({ monetary_options: [], session_options: [] });
    
    const [voucherType, setVoucherType] = useState('monetary');
    const [selectedAmount, setSelectedAmount] = useState(null);
    const [customAmount, setCustomAmount] = useState('');
    const [selectedSession, setSelectedSession] = useState(null);
    
    const [formData, setFormData] = useState({
        purchaser_name: '',
        purchaser_email: '',
        recipient_name: '',
        recipient_email: '',
        personal_message: ''
    });

    useEffect(function() {
        fetchOptions();
    }, []);

    const fetchOptions = async function() {
        try {
            const response = await axios.get(API + '/gift-vouchers/options');
            setOptions(response.data);
        } catch (error) {
            toast.error('Failed to load voucher options');
        } finally {
            setLoading(false);
        }
    };

    const getAmount = function() {
        if (voucherType === 'session' && selectedSession) {
            return selectedSession.price;
        }
        if (selectedAmount === 'custom') {
            return parseFloat(customAmount);
        }
        return selectedAmount;
    };

    const handleFormChange = function(field, value) {
        setFormData(function(prev) {
            return { ...prev, [field]: value };
        });
    };

    const handleSubmit = async function(e) {
        e.preventDefault();
        
        const amount = getAmount();
        if (!amount || amount <= 0) {
            toast.error('Please select or enter a valid amount');
            return;
        }
        
        if (voucherType === 'session' && !selectedSession) {
            toast.error('Please select a session');
            return;
        }

        setSubmitting(true);
        try {
            const requestData = {
                voucher_type: voucherType,
                purchaser_name: formData.purchaser_name,
                purchaser_email: formData.purchaser_email,
                recipient_name: formData.recipient_name,
                recipient_email: formData.recipient_email,
                personal_message: formData.personal_message
            };
            
            if (voucherType === 'monetary') {
                requestData.amount = amount;
            } else {
                requestData.session_id = selectedSession.id;
            }
            
            const response = await axios.post(API + '/gift-vouchers/create', requestData);
            
            if (response.data.checkout_url) {
                window.location.href = response.data.checkout_url;
            } else {
                toast.error('No checkout URL received');
                setSubmitting(false);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.detail || error.message || 'Failed to create voucher';
            toast.error(errorMsg);
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    const currentAmount = getAmount();

    return (
        <div className="min-h-screen bg-gradient-to-b from-secondary/30 to-background">
            <SEOHead 
                title={pageSEO.giftVouchers.title}
                description={pageSEO.giftVouchers.description}
                keywords={pageSEO.giftVouchers.keywords}
                canonical={pageSEO.giftVouchers.canonical}
                schema={generateBreadcrumbSchema([
                    { name: 'Home', url: '/' },
                    { name: 'Gift Vouchers', url: '/gift-vouchers' }
                ])}
            />
            <section className="py-16 px-4">
                <div className="container mx-auto max-w-4xl text-center">
                    <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                        <Gift className="w-4 h-4 mr-2" />
                        Gift Vouchers
                    </Badge>
                    <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                        Give the Gift of Adventure
                    </h1>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                        Treat someone special to an unforgettable Mantrailing experience with their dog. 
                        Our gift vouchers make the perfect present for dog lovers.
                    </p>
                </div>
            </section>

            <section className="pb-20 px-4">
                <div className="container mx-auto max-w-5xl">
                    <div className="grid lg:grid-cols-2 gap-8">
                        <div className="space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Sparkles className="w-5 h-5 text-primary" />
                                        Choose Voucher Type
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <VoucherTypeSelector 
                                        voucherType={voucherType}
                                        setVoucherType={setVoucherType}
                                        setSelectedSession={setSelectedSession}
                                        setSelectedAmount={setSelectedAmount}
                                        setCustomAmount={setCustomAmount}
                                    />
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle>
                                        {voucherType === 'monetary' ? 'Select Amount' : 'Select Session'}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {voucherType === 'monetary' ? (
                                        <MonetaryAmountSelector
                                            options={options.monetary_options}
                                            selectedAmount={selectedAmount}
                                            setSelectedAmount={setSelectedAmount}
                                            customAmount={customAmount}
                                            setCustomAmount={setCustomAmount}
                                        />
                                    ) : (
                                        <SessionSelector
                                            sessions={options.session_options}
                                            selectedSession={selectedSession}
                                            setSelectedSession={setSelectedSession}
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        <div>
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Heart className="w-5 h-5 text-red-500" />
                                        Voucher Details
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="purchaser_name">Your Name</Label>
                                                <Input
                                                    id="purchaser_name"
                                                    value={formData.purchaser_name}
                                                    onChange={function(e) { handleFormChange('purchaser_name', e.target.value); }}
                                                    required
                                                    data-testid="purchaser-name-input"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="purchaser_email">Your Email</Label>
                                                <Input
                                                    id="purchaser_email"
                                                    type="email"
                                                    value={formData.purchaser_email}
                                                    onChange={function(e) { handleFormChange('purchaser_email', e.target.value); }}
                                                    required
                                                    data-testid="purchaser-email-input"
                                                />
                                            </div>
                                        </div>
                                        
                                        <div className="border-t pt-4">
                                            <p className="text-sm font-medium text-muted-foreground mb-3">Recipient Details</p>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <Label htmlFor="recipient_name">Recipient Name</Label>
                                                    <Input
                                                        id="recipient_name"
                                                        value={formData.recipient_name}
                                                        onChange={function(e) { handleFormChange('recipient_name', e.target.value); }}
                                                        required
                                                        data-testid="recipient-name-input"
                                                    />
                                                </div>
                                                <div>
                                                    <Label htmlFor="recipient_email">Recipient Email</Label>
                                                    <Input
                                                        id="recipient_email"
                                                        type="email"
                                                        value={formData.recipient_email}
                                                        onChange={function(e) { handleFormChange('recipient_email', e.target.value); }}
                                                        required
                                                        data-testid="recipient-email-input"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <Label htmlFor="personal_message">Personal Message (Optional)</Label>
                                            <textarea
                                                id="personal_message"
                                                value={formData.personal_message}
                                                onChange={function(e) { handleFormChange('personal_message', e.target.value); }}
                                                placeholder="Add a personal message to your gift..."
                                                className="w-full px-4 py-3 border rounded-lg resize-none h-24"
                                                data-testid="personal-message-input"
                                            />
                                        </div>

                                        <div className="bg-secondary/50 rounded-xl p-4 space-y-2">
                                            <div className="flex justify-between items-center">
                                                <span className="text-muted-foreground">Voucher Type</span>
                                                <span className="font-medium capitalize">{voucherType}</span>
                                            </div>
                                            {voucherType === 'session' && selectedSession && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-muted-foreground">Session</span>
                                                    <span className="font-medium">{selectedSession.name}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between items-center pt-2 border-t">
                                                <span className="font-semibold">Total</span>
                                                <span className="text-2xl font-bold text-primary">
                                                    £{currentAmount || '0.00'}
                                                </span>
                                            </div>
                                        </div>

                                        <Button 
                                            type="submit" 
                                            className="w-full btn-primary py-6 text-lg"
                                            disabled={submitting || !currentAmount}
                                            data-testid="purchase-voucher-btn"
                                        >
                                            {submitting ? (
                                                <span className="flex items-center">
                                                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                                    Processing...
                                                </span>
                                            ) : (
                                                <span className="flex items-center">
                                                    <Gift className="w-5 h-5 mr-2" />
                                                    Purchase Gift Voucher
                                                </span>
                                            )}
                                        </Button>
                                        
                                        <p className="text-xs text-center text-muted-foreground">
                                            Secure payment powered by Stripe. The recipient will receive their voucher code via email.
                                        </p>
                                    </form>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 mt-12">
                        <Card className="text-center p-6">
                            <Gift className="w-10 h-10 text-primary mx-auto mb-3" />
                            <h3 className="font-semibold mb-2">Instant Delivery</h3>
                            <p className="text-sm text-muted-foreground">
                                Voucher code sent immediately via email after purchase
                            </p>
                        </Card>
                        <Card className="text-center p-6">
                            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
                            <h3 className="font-semibold mb-2">Easy to Redeem</h3>
                            <p className="text-sm text-muted-foreground">
                                Simply enter the code at checkout to redeem
                            </p>
                        </Card>
                        <Card className="text-center p-6">
                            <Heart className="w-10 h-10 text-red-500 mx-auto mb-3" />
                            <h3 className="font-semibold mb-2">Perfect Gift</h3>
                            <p className="text-sm text-muted-foreground">
                                An unforgettable experience for dog lovers
                            </p>
                        </Card>
                    </div>
                </div>
            </section>
        </div>
    );
}
